import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Wrench, Hammer, HardHat, TrendingUp, Search, Zap, Award, Star } from "lucide-react";
import Marquee from "../components/Marquee";
import HomepageAuditBlock from "../components/HomepageAuditBlock";
import OfferHero from "../components/OfferHero";
import PricingCards from "../components/PricingCards";
import Seo, { organizationSchema, websiteSchema, localBusinessSchema, faqSchema } from "../components/Seo";
import { SectionTitle, StatNumber } from "../components/Section";
import { lazyOnVisible, SectionSkeleton } from "../components/LazyOnVisible";
import { INDUSTRIES, REASONS } from "../lib/content";
import { useStats } from "../lib/hooks";
import { api } from "../lib/api";

// Below-the-fold sections — code-split + only mounted when scrolled into view.
const GrowthDiagram    = lazyOnVisible(() => import("../components/GrowthDiagram"),    { fallback: <SectionSkeleton h={520} /> });
const AuditTool        = lazyOnVisible(() => import("../components/AuditTool"),        { fallback: <SectionSkeleton h={640} /> });
const Testimonials     = lazyOnVisible(() => import("../components/Testimonials"),     { fallback: <SectionSkeleton h={360} /> });
const PortfolioShowcase = lazyOnVisible(() => import("../components/PortfolioShowcase"), { fallback: <SectionSkeleton h={700} /> });

// Home-service / trades-focused offer stack — repositioned Feb 2026.
const TRADE_SERVICES = [
  {
    title: "Lead-Generation Websites",
    description: "Websites built to turn visitors into booked jobs — clear calls, fast quote forms, mobile-first.",
    icon: Wrench,
    href: "/website-design",
    accent: "#FF6A00",
  },
  {
    title: "Local SEO for Trades",
    description: "Rank in your service area. Google Business Profile, city pages, and reviews that drive calls.",
    icon: Search,
    href: "/seo-ai-search-optimization",
    accent: "#0066FF",
  },
  {
    title: "Enquiry Automation",
    description: "Instant email + WhatsApp alerts, automatic replies, and a CRM so no lead falls through the cracks.",
    icon: Zap,
    href: "/ai-automation",
    accent: "#8B5CF6",
  },
  {
    title: "Construction & Contractor Sites",
    description: "Premium websites for builders, contractors, and construction firms — designed to win larger projects.",
    icon: HardHat,
    href: "/construction-marketing",
    accent: "#22C55E",
  },
  {
    title: "Trades & Home-Service Sites",
    description: "Plumbing, electrical, HVAC, roofing, cleaning, landscaping — websites tuned to your service.",
    icon: Hammer,
    href: "/trades-websites",
    accent: "#EC4899",
  },
  {
    title: "Growth Retainers",
    description: "Ongoing SEO, content, and conversion improvements — every month you rank higher and book more.",
    icon: TrendingUp,
    href: "/ai-automation",
    accent: "#F59E0B",
  },
];

const HOME_FAQS = [
  { q: "Who is WebsiteAndGo built for?", a: "We specialise in home-service, trade, and construction businesses in the US and UK — plumbers, electricians, HVAC, roofers, builders, contractors, cleaners, landscapers, and other service trades. If you rely on local customers finding and calling you, we're a great fit." },
  { q: "How long until my new website goes live?", a: "Our $199 Starter Site is delivered in 7 days. Larger lead-gen and multi-service sites take longer — we confirm the exact timeline with you before we start." },
  { q: "Will you help me rank on Google in my service area?", a: "Yes — every website we build includes local SEO foundations (Google Business Profile setup, city/service landing pages, schema, review integration). We also offer ongoing SEO retainers to compound results month over month." },
  { q: "Do you charge upfront or on a payment plan?", a: "Both options are available. Most clients start with a fixed one-time build price. For growth retainers (SEO + automation), we invoice monthly." },
  { q: "How much does a small business website cost?", a: "Our complete Starter Site is $199 one-time and is delivered in 7 days, with a full refund if you're unhappy before launch. The Lead Gen Site is $499 one-time, and Local SEO retainers start at $199/month." },
  { q: "Do you work with businesses in the UK as well as the USA?", a: "Yes. We have offices in Lincoln Metro, Nebraska (+1 808-998-5633) and Luton, England (+44 7463 638059) and work remotely with small businesses across both countries." },
  { q: "Can I see real examples of your work?", a: "Yes — request a free website review and we'll show you real trade / home-service portfolios during the call. We won't fake numbers or reviews on our own site." },
];

export default function Home() {
  const stats = useStats();
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/pricing-packages").then((r) => { if (alive) setPackages(r.data?.packages || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div data-testid="page-home">
      <Seo
        title="Affordable Small Business Website Design from $199 | USA & UK | WebsiteAndGo"
        description="Get a complete, mobile-friendly business website for $199, delivered in 7 days with a money-back guarantee. Web design and local SEO for small businesses across the USA & UK."
        path="/"
        schema={{ "@context": "https://schema.org", "@graph": [organizationSchema, websiteSchema, localBusinessSchema, faqSchema(HOME_FAQS)] }}
      />
      <OfferHero />

      {/* MARQUEE */}
      <Marquee />

      {/* FREE WEBSITE GROWTH AUDIT — compact conversion block below hero */}
      <HomepageAuditBlock />

      {/* SERVICES — trade & home-service focused */}
      <section className="bg-[#050505] py-20" data-testid="section-services">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <SectionTitle
              eyebrow="What we do"
              title="Everything a trade business needs to book more jobs online."
              kicker="Lead-gen websites, local SEO, and enquiry automation — built for plumbers, electricians, HVAC, roofers, builders, contractors, and other home-service trades."
            />
            <Link to="/services" className="btn-ghost shrink-0 self-start" data-testid="services-view-all">
              All services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TRADE_SERVICES.map((s, i) => {
              const Icon = s.icon;
              const slug = s.title.toLowerCase().replace(/\s+/g, "-");
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 3) * 0.05 }}
                  whileHover={{ y: -4 }}
                >
                  <Link
                    to={s.href}
                    data-testid={`service-card-${slug}`}
                    className="group block bg-[#0A0A0A] border border-white/10 rounded-2xl p-7 h-full hover:border-[#FF6A00]/60 transition-all duration-300"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition border border-white/10"
                      style={{ background: `${s.accent}22` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: s.accent }} />
                    </div>
                    <div className="font-display font-semibold text-lg tracking-tight">{s.title}</div>
                    <div className="mt-2 text-sm text-white/65 leading-relaxed">{s.description}</div>
                    <div className="mt-5 inline-flex items-center gap-1 text-xs font-mono text-white/50 group-hover:text-[#FF6A00] transition">
                      LEARN MORE <ArrowUpRight className="w-3 h-3" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING (moved directly beneath Services per user request) */}
      <section id="pricing" className="bg-[#F5F5F5] text-[#050505] py-24 scroll-mt-20" data-testid="section-pricing">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionTitle
            dark
            eyebrow="Pricing"
            title="Transparent. Premium. Outcome focused."
            kicker="Fixed prices, no surprises. Choose what fits your stage."
            align="center"
          />
          <div className="mt-14">
            <PricingCards packages={packages} />
          </div>
          <p className="mt-8 text-center text-xs text-black/50" data-testid="pricing-guarantee-note">
            Starter Site money-back guarantee: full refund if you&apos;re not happy with your website before it goes live.
          </p>
        </div>
      </section>

      {/* PORTFOLIO SHOWCASE — static, homepage only */}
      <div id="portfolio">
        <PortfolioShowcase />
      </div>

      {/* WHY US */}
      <section className="bg-[#F5F5F5] text-[#050505] py-20" data-testid="section-why-us">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionTitle
            dark
            eyebrow="Why us"
            title="Why ambitious businesses choose WebsiteAndGo."
            kicker="We combine premium design, AI technology, SEO, and automation to help businesses attract more customers and operate smarter."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-black/5 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                data-testid={`reason-card-${i}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#050505] text-white font-display font-bold flex items-center justify-center">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-5 font-display font-bold text-xl tracking-tight">{r.title}</div>
                <div className="mt-2 text-black/60 leading-relaxed">{r.text}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI GROWTH SYSTEM */}
      <section className="bg-[#050505] py-24 relative overflow-hidden" data-testid="section-growth-system">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#0066FF]/10 blur-[160px]" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <SectionTitle
            eyebrow="The AI Growth System"
            title="Website → SEO → AI Search → Automation → Customers."
            kicker="Each piece reinforces the next. That's how compounding growth happens."
            align="center"
          />
          <GrowthDiagram />
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-[#F5F5F5] text-[#050505] py-24" data-testid="section-industries">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <SectionTitle dark eyebrow="Industries" title="We know your sector." />
            <Link to="/industries" className="btn-ghost-dark shrink-0 self-start" data-testid="industries-view-all">
              All industries <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.slice(0, 6).map((ind) => (
              <Link
                to="/industries"
                key={ind.slug}
                data-testid={`industry-card-${ind.slug}`}
                className="group relative overflow-hidden rounded-2xl bg-black aspect-[4/5]"
              >
                <img
                  src={ind.image}
                  alt={ind.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 group-hover:opacity-70 transition duration-700"
                 loading="lazy" decoding="async"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <div className="font-display text-2xl font-bold tracking-tight">{ind.title}</div>
                  <div className="mt-2 text-sm text-white/80 max-w-xs">{ind.promise}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES — removed from homepage per redesign spec; lives on /case-studies */}

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* FAQ */}
      <section className="bg-[#050505] py-24" data-testid="section-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <SectionTitle eyebrow="FAQ" title="Frequently asked questions." align="center" />
          <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
            {HOME_FAQS.map((f) => (
              <details key={f.q} className="py-5 group" data-testid={`faq-${f.q.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
                <summary className="font-display font-semibold text-lg cursor-pointer flex items-start justify-between gap-4 list-none">
                  <span>{f.q}</span>
                  <span className="text-[#FF6A00] group-open:rotate-45 transition-transform text-2xl leading-none shrink-0">+</span>
                </summary>
                <div className="mt-3 text-white/70 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — duplicate removed (now appears before Pricing per spec) */}

      {/* FINAL CTA */}
      <section className="relative bg-[#050505] py-28 overflow-hidden" data-testid="section-final-cta">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#FF6A00]/15 blur-[180px]" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative text-center">
          <Sparkles className="w-8 h-8 mx-auto text-[#FF6A00]" />
          <h2 className="mt-6 font-display font-bold tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Ready to turn your website into a <span className="text-gradient-bo">lead-generation system?</span>
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
            Get a free, no-obligation website audit. We&apos;ll show you exactly why your website isn&apos;t generating more calls — and what to fix first.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/audit" className="btn-primary" data-testid="final-cta-primary">
              Get My Free Website Audit <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-ghost" data-testid="final-cta-secondary">
              Book a Strategy Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
